import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Prediction } from '../../../types/predictions';
import { Theme } from '../../../theme';

// MSTR-specific prediction styles
const mstrPredictionStyles = {
  colors: {
    success: 'rgba(76, 175, 80, 0.9)',  // Green
    successSolid: 'rgba(76, 175, 80, 1)',
    miss: 'rgba(244, 67, 54, 0.9)',     // Red  
    missSolid: 'rgba(244, 67, 54, 1)',
    pending: 'rgba(255, 152, 0, 0.9)',  // Orange
    pendingSolid: 'rgba(255, 152, 0, 1)',
  }
};

// Helper function to get status-based color for MSTR predictions
const getMSTRStatusColor = (prediction: Prediction, solid = false) => {
  if (prediction.success_status === 'success') {
    return solid ? mstrPredictionStyles.colors.successSolid : mstrPredictionStyles.colors.success;
  } else if (prediction.success_status === 'miss') {
    return solid ? mstrPredictionStyles.colors.missSolid : mstrPredictionStyles.colors.miss;
  } else {
    return solid ? mstrPredictionStyles.colors.pendingSolid : mstrPredictionStyles.colors.pending;
  }
};

// Helper function to find crossing point for MSTR predictions
const findMSTRCrossingPoint = (
  chartData: Array<{ time: Date; value: number }>,
  prediction: Prediction
): { time: Date; value: number } | null => {
  if (!chartData.length) return null;

  let targetPrice: number | null = null;
  
  if (prediction.prediction_type === 'target') {
    const priceValue = prediction.price_target || prediction.price_min || prediction.price_prediction;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'top') {
    // TOP predictions use price_max as the target level
    const priceValue = prediction.price_max ?? prediction.price_target;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'bottom') {
    // BOTTOM predictions use price_min as the target level  
    const priceValue = prediction.price_min ?? prediction.price_target;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'pricetimerange') {
    targetPrice = prediction.price_min !== undefined ? prediction.price_min : null;
  } else if (prediction.prediction_type === 'range') {
    targetPrice = prediction.price_min !== undefined ? prediction.price_min : null;
  }

  if (targetPrice === null || targetPrice === undefined) return null;

  // Sort data chronologically
  const sortedData = [...chartData].sort((a, b) => a.time.getTime() - b.time.getTime());
  
  // Get the valid time range for this prediction
  const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
  
  // Find where price crosses the target WITHIN the valid time range
  for (let i = 1; i < sortedData.length; i++) {
    const prev = sortedData[i-1];
    const curr = sortedData[i];
    
    // Skip if current point is outside the prediction's time range
    if (startDate && curr.time < startDate) continue;
    
    // Check if price crosses target between these points
    if (prev.value < targetPrice && curr.value >= targetPrice) {
      return curr;
    }
  }
  
  return null;
};

interface MSTRMobilePriceChartProps {
  predictions: Prediction[];
  activePredictions: number[];
  chartData: Array<{ time: string; value: number }>;
  isDarkMode: boolean;
  theme: Theme;
}

export const MSTRMobilePriceChart: React.FC<MSTRMobilePriceChartProps> = ({
  predictions,
  activePredictions,
  chartData,
  isDarkMode,
  theme
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || chartData.length === 0) return;


    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Mobile-optimized dimensions - FIXED: Account for container padding/border
    const containerWidth = svgRef.current.clientWidth;
    const containerHeight = svgRef.current.clientHeight;
    const margin = { top: 15, right: 15, bottom: 25, left: 45 };
    const width = Math.max(0, containerWidth - margin.left - margin.right); // FIXED: Prevent negative width
    const height = Math.max(0, containerHeight - margin.top - margin.bottom); // FIXED: Prevent negative height


    if (width <= 0 || height <= 0) {
      console.warn('📊 MSTR Mobile Chart: Invalid dimensions, skipping render');
      return;
    }

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Add clipping path
    const clipId = `mstr-chart-clip-${Math.random().toString(36).substr(2, 9)}`;
    svg.append("defs").append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width)
      .attr("height", height);

    const chartArea = g.append("g")
      .attr("clip-path", `url(#${clipId})`);

    // Parse and sort MSTR data
    const parseTime = d3.timeParse("%Y-%m-%d");
    const processedData = chartData
      .map(d => ({
        ...d,
        time: parseTime(d.time) || new Date(d.time),
        value: +d.value
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    if (processedData.length === 0) return;

    // MSTR-SPECIFIC Y-AXIS SCALING: Much tighter for lower stock prices
    const prices = processedData.map(d => d.value);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    
    // Get active MSTR prediction prices to adjust Y-axis if needed
    const activePredictionPrices = activePredictions
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p)
      .map(p => {
        if (p!.prediction_type === 'target') {
          const priceValue = p!.price_target ?? p!.price_min ?? p!.price_prediction;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'range') {
          return p!.price_max ?? p!.price_min ?? null;
        }
        return null;
      })
      .filter(price => price !== null && price !== undefined && isFinite(price)) as number[];
    
    // Include prediction prices in the range calculation
    const allPrices = activePredictionPrices.length > 0 ? [...prices, ...activePredictionPrices] : prices;
    const finalMinPrice = Math.min(...allPrices);
    const finalMaxPrice = Math.max(...allPrices);
    const finalPriceRange = finalMaxPrice - finalMinPrice;
    
    // MSTR: Adjust scaling based on prediction values
    const padding = Math.max(finalPriceRange * 0.15, 10); // 15% padding or $10 minimum
    let yMin = finalMinPrice - padding;
    let yMax = finalMaxPrice + padding;
    
    // Allow negative values if needed (e.g., for zero or negative predictions)
    if (activePredictionPrices.some(price => price <= 0)) {
      yMin = Math.min(yMin, Math.min(...activePredictionPrices) - 10);
    } else {
      yMin = Math.max(yMin, 0); // Floor at $0 for normal cases
    }
    

    // MSTR-SPECIFIC TIME WINDOW: Show full current year + future predictions
    let timeExtent = d3.extent(processedData, d => d.time) as [Date, Date];
    
    // Get active MSTR prediction dates
    const activePredictionDates: Date[] = activePredictions
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p)
      .flatMap(p => {
        const dates: Date[] = [];
        
        if (p!.prediction_type === 'pricetimerange' && p!.start_date && p!.end_date) {
          dates.push(new Date(p!.start_date));
          dates.push(new Date(p!.end_date));
        }
        else if (p!.target_time) {
          dates.push(new Date(p!.target_time));
        }
        else if (p!.end_date) {
          dates.push(new Date(p!.end_date));
        }
        
        return dates.filter(date => !isNaN(date.getTime()));
      });
    
    // IMPROVED TIME WINDOW: Show at least full current year for MSTR
    const currentYear = new Date().getFullYear();
    const startOfCurrentYear = new Date(`${currentYear}-01-01`);
    const endOfCurrentYear = new Date(`${currentYear}-12-31`);
    
    if (activePredictionDates.length > 0) {
      // When predictions are active: Show from start of current year to prediction end + padding
      const latestPrediction = new Date(Math.max(...activePredictionDates.map(d => d.getTime())));
      
      // Start from beginning of current year or earliest data, whichever is later
      const smartStartDate = new Date(Math.min(startOfCurrentYear.getTime(), timeExtent[0].getTime()));
      
      // End at prediction date with padding, but at least end of current year
      const paddingMonths = 3; // 3 months padding after predictions
      const predictionEndWithPadding = new Date(latestPrediction.getTime() + (paddingMonths * 30 * 24 * 60 * 60 * 1000));
      const smartEndDate = new Date(Math.max(endOfCurrentYear.getTime(), predictionEndWithPadding.getTime()));
      
      timeExtent = [smartStartDate, smartEndDate];
    } else {
      // No active predictions: Show full current year + some future
      const dataStartDate = timeExtent[0];
      const dataEndDate = timeExtent[1];
      
      // Start from beginning of current year, or earlier if we have older data
      const smartStartDate = new Date(Math.min(startOfCurrentYear.getTime(), dataStartDate.getTime()));
      
      // End at least 6 months into the future from now for better context
      const futureMonths = 6;
      const futurePadding = futureMonths * 30 * 24 * 60 * 60 * 1000;
      const smartEndDate = new Date(Math.max(dataEndDate.getTime() + futurePadding, endOfCurrentYear.getTime()));
      
      timeExtent = [smartStartDate, smartEndDate];
    }

    const xScale = d3.scaleTime()
      .domain(timeExtent)
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([height, 0]);

    // IMPROVED X-AXIS: Better time formatting for MSTR
    const displayTimeExtent = timeExtent;
    const yearSpan = displayTimeExtent[1].getFullYear() - displayTimeExtent[0].getFullYear();
    const monthSpan = (displayTimeExtent[1].getTime() - displayTimeExtent[0].getTime()) / (1000 * 60 * 60 * 24 * 30);
    
    let tickFormat: (date: Date) => string;
    let tickCount: number;
    
    if (yearSpan >= 2) {
      // Multiple years: Show years only
      tickFormat = d3.timeFormat("%Y");
      tickCount = Math.min(yearSpan + 1, 4);
    } else if (monthSpan > 6) {
      // Single year or more than 6 months: Show quarters
      tickFormat = d3.timeFormat("%b %Y");
      tickCount = 4;
    } else {
      // Less than 6 months: Show months
      tickFormat = d3.timeFormat("%b");
      tickCount = Math.min(Math.ceil(monthSpan), 6);
    }

    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .tickFormat(tickFormat as any)
        .ticks(tickCount))
      .selectAll("text")
      .style("fill", theme.text)
      .style("font-size", "11px");

    // Y axis with MSTR-specific formatting (show actual dollar amounts)
    g.append("g")
      .call(d3.axisLeft(yScale)
        .tickFormat(d => `$${d}`) // MSTR: Show actual dollar amounts
        .ticks(5))
      .selectAll("text")
      .style("fill", theme.text)
      .style("font-size", "11px");

    // Style axis lines
    g.selectAll(".domain, .tick line")
      .style("stroke", theme.border);

    // Add horizontal grid lines (Y-axis)
    chartArea.selectAll(".y-grid-line")
      .data(yScale.ticks(6))
      .enter().append("line")
      .attr("class", "y-grid-line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => yScale(d))
      .attr("y2", d => yScale(d))
      .style("stroke", theme.border)
      .style("stroke-opacity", 0.4)
      .style("stroke-width", 1);

    // Add vertical grid lines (X-axis)
    chartArea.selectAll(".x-grid-line")
      .data(xScale.ticks(5))
      .enter().append("line")
      .attr("class", "x-grid-line")
      .attr("x1", d => xScale(d))
      .attr("x2", d => xScale(d))
      .attr("y1", 0)
      .attr("y2", height)
      .style("stroke", theme.border)
      .style("stroke-opacity", 0.25)
      .style("stroke-width", 1);

    // Create line generator
    const line = d3.line<typeof processedData[0]>()
      .x(d => xScale(d.time))
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX);

    // Add the main MSTR price line
    chartArea.append("path")
      .datum(processedData)
      .attr("fill", "none")
      .attr("stroke", theme.primary) // Use theme primary instead of hardcoded blue
      .attr("stroke-width", 2.5)
      .attr("d", line);

    // Add watermark to the chart
    svg.append('g')
      .attr('class', 'chart-watermark')
      .attr('transform', `translate(${width / 2 + margin.left}, ${height / 2 + margin.top})`)
      .style('pointer-events', 'none') // Make sure it doesn't capture touch events
      .call(g => {
        // Add "proofofprice.com" watermark text
        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('font-size', '20px')
          .attr('font-weight', 'bold')
          .attr('font-family', 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif')
          .attr('fill', theme.text)
          .attr('opacity', 0.06)
          .attr('transform', 'rotate(-15)')
          .text('proofofprice.com');
      });

    // Add current MSTR price indicator
    const latestPoint = processedData[processedData.length - 1];
    if (latestPoint) {
      const currentX = xScale(latestPoint.time);
      const currentY = yScale(latestPoint.value);

      if (currentX >= 0 && currentX <= width) {
        // Current price dot
        chartArea.append("circle")
          .attr("cx", currentX)
          .attr("cy", currentY)
          .attr("r", 4)
          .attr("fill", theme.primary)
          .attr("stroke", theme.surface)
          .attr("stroke-width", 2);

        // Current price label with MSTR formatting
        chartArea.append("text")
          .attr("x", currentX - 8)
          .attr("y", currentY - 8)
          .style("fill", theme.primary)
          .style("font-size", "12px")
          .style("font-weight", "bold")
          .style("text-anchor", "end")
          .text(`$${latestPoint.value.toFixed(0)}`); // MSTR: $500 format
      }
    }

    // MSTR PREDICTION RENDERING - Use separate functions
    
    // Render each prediction type separately with proper error handling
    try {
      renderMSTRTargetPredictions(chartArea, predictions, activePredictions, xScale, yScale, theme);
      renderMSTRRangePredictions(chartArea, predictions, activePredictions, xScale, yScale, theme);
      renderMSTRPriceTimeRangePredictions(chartArea, predictions, activePredictions, xScale, yScale, theme);
    } catch (error) {
      console.error('Error rendering MSTR predictions:', error);
    }

    // Success crossing points for MSTR
    const successfulPredictions = activePredictions
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p && p.success_status === 'success');

    successfulPredictions.forEach(prediction => {
      if (!prediction) return;
      
      const crossingPoint = findMSTRCrossingPoint(processedData, prediction);
      
      if (crossingPoint) {
        const crossX = xScale(crossingPoint.time);
        const crossY = yScale(crossingPoint.value);
        
        // Large green success dot
        chartArea.append("circle")
          .attr("cx", crossX)
          .attr("cy", crossY)
          .attr("r", 6)
          .attr("fill", mstrPredictionStyles.colors.successSolid)
          .attr("stroke", "white")
          .attr("stroke-width", 2)
          .style("opacity", 1);
        
        // Pulse ring
        chartArea.append("circle")
          .attr("cx", crossX)
          .attr("cy", crossY)
          .attr("r", 8)
          .attr("fill", "none")
          .attr("stroke", mstrPredictionStyles.colors.successSolid)
          .attr("stroke-width", 1)
          .attr("stroke-opacity", 0.3);
        
        // Success label
        chartArea.append("text")
          .attr("x", crossX - 8)
          .attr("y", crossY - 12)
          .style("fill", mstrPredictionStyles.colors.successSolid)
          .style("font-size", "9px")
          .style("font-weight", "bold")
          .style("opacity", 0.9)
          .style("text-anchor", "end")
          .text(`✓ ${crossingPoint.time.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`);
      }
    });

  }, [chartData, predictions, activePredictions, isDarkMode, theme]);

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      style={{ 
        background: 'transparent',
        display: 'block',
        boxSizing: 'border-box',
        // Safari compatibility improvements
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        // Prevent rubber band scrolling on iOS Safari
        overscrollBehavior: 'contain',
        touchAction: 'pan-x pan-y'
      }}
    />
  );
};

// MSTR Target Prediction Renderer
const renderMSTRTargetPredictions = (
  chartArea: any,
  predictions: Prediction[],
  activePredictions: number[],
  xScale: any,
  yScale: any,
  theme: Theme
) => {
  activePredictions.forEach(predictionId => {
    const prediction = predictions.find(p => p.id === predictionId && p.prediction_type === 'target');
    if (!prediction) return;

    // Safe number conversion - use nullish coalescing to handle zero values
    let predictionPrice: number | null = null;
    const priceValue = prediction.price_target ?? prediction.price_min ?? prediction.price_prediction;
    
    if (typeof priceValue === 'string') {
      predictionPrice = parseFloat(priceValue);
    } else if (typeof priceValue === 'number') {
      predictionPrice = priceValue;
    } else {
      predictionPrice = null;
    }
    
    if (predictionPrice === null || predictionPrice === undefined || isNaN(predictionPrice)) return;
    
    const predictionDate = new Date(prediction.target_time || prediction.end_date || new Date());
    if (isNaN(predictionDate.getTime())) return;

    const predX = xScale(predictionDate);
    const predY = yScale(predictionPrice);
    
    // Status-based prediction dot
    chartArea.append("circle")
      .attr("cx", predX)
      .attr("cy", predY)
      .attr("r", 4)
      .attr("fill", getMSTRStatusColor(prediction, true))
      .attr("stroke", theme.surface)
      .attr("stroke-width", 2)
      .style("opacity", 0.9);
    
    // MSTR-specific label formatting
    chartArea.append("text")
      .attr("x", predX - 8)
      .attr("y", predY - 8)
      .style("fill", getMSTRStatusColor(prediction, true))
      .style("font-size", "10px")
      .style("font-weight", "normal")
      .style("opacity", 0.9)
      .style("text-anchor", "end")
      .text(`$${predictionPrice.toFixed(0)}`);
  });
};

// MSTR Range Prediction Renderer
const renderMSTRRangePredictions = (
  chartArea: any,
  predictions: Prediction[],
  activePredictions: number[],
  xScale: any,
  yScale: any,
  theme: Theme
) => {
  activePredictions.forEach(predictionId => {
    const prediction = predictions.find(p => p.id === predictionId && p.prediction_type === 'range');
    if (!prediction) return;

    // Safe number conversion
    const priceMin = typeof prediction.price_min === 'number' ? prediction.price_min : 
                    typeof prediction.price_min === 'string' ? parseFloat(prediction.price_min) : null;
    const priceMax = typeof prediction.price_max === 'number' ? prediction.price_max : 
                    typeof prediction.price_max === 'string' ? parseFloat(prediction.price_max) : null;
    
    if (!priceMin || !priceMax || priceMin <= 0 || priceMax <= 0 || isNaN(priceMin) || isNaN(priceMax)) return;
    
    const startDate = new Date(prediction.start_date || new Date());
    const endDate = new Date(prediction.end_date || prediction.target_time || new Date());
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;

    const x1 = xScale(startDate);
    const x2 = xScale(endDate);
    const y1 = yScale(priceMax);
    const y2 = yScale(priceMin);
    
    // Status-based transparent rectangle
    chartArea.append("rect")
      .attr("x", Math.min(x1, x2))
      .attr("y", y1)
      .attr("width", Math.abs(x2 - x1))
      .attr("height", Math.abs(y2 - y1))
      .attr("fill", getMSTRStatusColor(prediction))
      .attr("fill-opacity", 0.1)
      .attr("stroke", getMSTRStatusColor(prediction, true))
      .attr("stroke-width", 1)
      .attr("stroke-opacity", 0.6)
      .attr("rx", 2);
    
    // MSTR-specific range label
    const leftX = Math.min(x1, x2);
    const centerY = (y1 + y2) / 2;
    
    chartArea.append("text")
      .attr("x", leftX - 8)
      .attr("y", centerY)
      .style("fill", getMSTRStatusColor(prediction, true))
      .style("font-size", "10px")
      .style("font-weight", "bold")
      .style("opacity", 0.8)
      .style("text-anchor", "end")
      .text(`$${priceMin.toFixed(0)}-$${priceMax.toFixed(0)}`);
  });
};

// MSTR PriceTimeRange Prediction Renderer
const renderMSTRPriceTimeRangePredictions = (
  chartArea: any,
  predictions: Prediction[],
  activePredictions: number[],
  xScale: any,
  yScale: any,
  theme: Theme
) => {
  activePredictions.forEach(predictionId => {
    const prediction = predictions.find(p => p.id === predictionId && p.prediction_type === 'pricetimerange');
    if (!prediction) return;

    // Safe number conversion
    const targetPrice = typeof prediction.price_min === 'number' ? prediction.price_min : 
                       typeof prediction.price_min === 'string' ? parseFloat(prediction.price_min) : null;
    
    if (!targetPrice || targetPrice <= 0 || isNaN(targetPrice)) return;
    
    const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
    const endDate = prediction.end_date ? new Date(prediction.end_date) : 
                   prediction.target_time ? new Date(prediction.target_time) : null;
    
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;

    const x1 = xScale(startDate);
    const x2 = xScale(endDate);
    const y = yScale(targetPrice);
    
    // Status-based horizontal dashed line
    chartArea.append("line")
      .attr("x1", Math.min(x1, x2))
      .attr("x2", Math.max(x1, x2))
      .attr("y1", y)
      .attr("y2", y)
      .attr("stroke", getMSTRStatusColor(prediction, true))
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "4,3")
      .attr("stroke-opacity", 0.8);
    
    // MSTR-specific pricetime label
    chartArea.append("text")
      .attr("x", Math.min(x1, x2) - 8)
      .attr("y", y - 5)
      .style("fill", getMSTRStatusColor(prediction, true))
      .style("font-size", "10px")
      .style("font-weight", "normal")
      .style("opacity", 0.8)
      .style("text-anchor", "end")
      .text(`$${targetPrice.toFixed(0)}`);
  });
};

export default MSTRMobilePriceChart;
